<script setup lang="ts">
import { nextTick } from 'vue';
import type { HeadlineGroup, ModuleCategory } from '~/types/config';
import { useConfigParserStore } from '~/stores/configParser';
import ConfigFormFields from '~/components/base/forms/ConfigFormFields.vue';

const configParserStore = useConfigParserStore();

const props = withDefaults(defineProps<{
  headlineGroups: HeadlineGroup[];
  categoryGroup: ModuleCategory;
  openAccordionTabs: number[];
  getNextInputId: (element: any, categoryGroup: any) => string | null;
  configValues: Record<string, string>;
  profileSettings?: boolean;
  readOnly?: boolean;
}>(), {
  profileSettings: false,
  readOnly: false,
});

const emit = defineEmits<{
  'update:activeIndex': [tabs: number[]];
  'focus:next': [nextId: string];
}>();

/**
 * Handle accordion tab state change
 */
const handleAccordionChange = (openTabs: number[]) => {
  emit('update:activeIndex', openTabs);
};

/**
 * Updates the enable flag for an element
 */
const updateEnableFlag = (elementId: number, enabled: boolean) => {
  if (elementId) {
    const enableValue = enabled ? '1' : '';
    configParserStore.updateElementValue(`enable_${elementId}`, enableValue);
  }
};

/**
 * Enables or disables all elements in the headline group
 */
const updateAllEnableFlags = async (headlineGroup: HeadlineGroup, enabled: boolean) => {
  headlineGroup.elements.forEach(el => {
    updateEnableFlag(el.id, enabled);
    if (enabled && !props.configValues[el.elementPath] && el.elementValue) {
      configParserStore.updateElementValue(el.elementPath, el.elementValue);
    }
  });
  await nextTick();
};

/**
 * Handle focus next from ConfigFormFields
 */
const handleFocusNext = (nextId: string) => {
  emit('focus:next', nextId);
};
</script>

<template>
  <!-- No headline groups -->
  <Message
    v-if="!headlineGroups || headlineGroups.length === 0"
    severity="warn"
    :closable="false"
    class="text-center"
  >
    {{ $t('config.form.noElements') }}
  </Message>

  <!-- Updated Accordion -->
  <Accordion
    v-else
    multiple
    :value="openAccordionTabs"
    @update:value="handleAccordionChange"
    class="mb-3"
  >
    <AccordionPanel
      v-for="(headlineGroup, groupIndex) in headlineGroups"
      :key="`group-${groupIndex}`"
      :value="groupIndex"
    >
      <AccordionHeader>
        <span class="text-lg font-semibold">
          {{ headlineGroup.headline }}
        </span>
      </AccordionHeader>

      <AccordionContent>
        <div class="p-4">
          <div
            v-for="(element, elementIndex) in headlineGroup.elements"
            :key="`element-${element.id}`"
            class="col-span-12 p-2 flex items-center"
          >
            <div style="flex: 1;">
              <ConfigFormFields
                :element="element"
                :get-next-input-id="getNextInputId"
                :category-group="categoryGroup"
                :config-values="configValues"
                :is-enabled="!profileSettings || configValues[`enable_${element.id}`] === '1'"
                :read-only="props.readOnly"
                @focus:next="handleFocusNext"
              />
            </div>

            <div style="width: 32px; display: flex; justify-content: center;">
              <input
                v-if="profileSettings && (categoryGroup.categoryName === 'settings' ||
                      (categoryGroup.categoryName !== 'settings' && elementIndex === 0))"
                type="checkbox"
                :id="`enable-${element.id}`"
                :checked="categoryGroup.categoryName === 'settings'
                  ? configValues[`enable_${element.id}`] === '1'
                  : headlineGroup.elements.every((el:any) => configValues[`enable_${el.id}`] === '1')"
                @change="(e:any) => {
                  if (categoryGroup.categoryName === 'settings') {
                    updateEnableFlag(element.id, e.target.checked);
                  } else if (elementIndex === 0) {
                    updateAllEnableFlags(headlineGroup, e.target.checked);
                  }
                }"
                class="ml-2"
              />
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionPanel>
  </Accordion>
</template>

<style scoped>
:deep(.p-accordionpanel) {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  overflow: hidden;
}

:deep(.p-accordionheader) {
  background-color: #f8fafc;
  border: none;
  border-radius: 0;
  padding: 0.75rem 1rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  user-select: none;
}

:deep(.p-accordioncontent) {
  background-color: #ffffff;
  border: none;
  padding: 0;
}

/* Arrow icon alignment and style */
:deep(.p-accordionheader .p-accordion-toggle-icon) {
  margin-left: auto;
  font-size: 1.25rem;
  color: #64748b;
  transition: transform 0.2s ease;
}
</style>
